import { CardsDataSourceBase } from '../../common/CardsDataSourceBase';
import { injectable } from 'inversify';
import { injectToken } from 'inversify-token';
import { YinzCamAPIResponse } from 'yinzcam-api';
import { YinzCamAppServerToken } from 'yinzcam-appserver';

// Mirrors the Janus 4 transform at yinzcam-web-janus4/src/lib/sources/
// YinzCamAppServerGameManhattan: reads ManhattanChart.Innings.Inning from the
// /Game/Manhattan/{gameId} feed and emits the `innings[]` shape that
// ManhattanChartAtom consumes (matching its `innings` spec entry).
function asArray<T>(value: T | T[] | undefined | null): T[] {
  return [].concat(value as any).filter((item) => item) as T[];
}

function toNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function ordinal(value: number): string {
  if (value === 1) return '1ST';
  if (value === 2) return '2ND';
  if (value === 3) return '3RD';
  return `${value}TH`;
}

@injectable()
export class YinzCamAppServerGameManhattan extends CardsDataSourceBase {
  public constructor(@injectToken(YinzCamAppServerToken) server) {
    super({ server });
  }

  protected processResponse(
    path: string,
    response: YinzCamAPIResponse,
    specData?: { [key: string]: any }
  ): object | any[] {
    const root = (response?.data as any)?.ManhattanChart;
    const teamInningCounts: Record<string, number> = {};

    const innings = asArray(root?.Innings?.Inning).map((inning: any) => {
      const attrs = inning?._attributes || {};
      const teamKey = attrs.TeamId || attrs.TeamTriCode || attrs.TeamName || attrs.Number;
      teamInningCounts[teamKey] = (teamInningCounts[teamKey] || 0) + 1;
      const battingInning = teamInningCounts[teamKey];
      const teamTriCode = attrs.TeamTriCode || attrs.TeamName || '';

      return {
        number: toNumber(attrs.Number, battingInning),
        battingInning,
        teamId: `${attrs.TeamId || ''}`,
        teamName: `${attrs.TeamName || ''}`,
        teamTriCode: `${teamTriCode}`,
        label: `${teamTriCode} ${ordinal(battingInning)}`.trim(),
        score: `${attrs.Score || ''}`,
        oversDisplay: `${attrs.OversDisplay || ''}`,
        overs: asArray(inning?.Overs?.Over).map((over: any) => {
          const overAttrs = over?._attributes || {};
          const fourRuns = toNumber(overAttrs.FourRuns);
          const sixRuns = toNumber(overAttrs.SixRuns);
          const boundaryRuns = fourRuns + sixRuns;
          const totalRuns = toNumber(overAttrs.TotalRuns);
          const providedOtherRuns = toNumber(overAttrs.OtherRuns, Number.NaN);

          return {
            over: toNumber(overAttrs.Over),
            totalRuns,
            wickets: toNumber(overAttrs.Wickets),
            fourRuns,
            sixRuns,
            boundaryRuns,
            // Prefer the upstream OtherRuns if it's a real number; otherwise
            // back-derive (total - boundary). Clamped at 0 so an inconsistent
            // upstream split can't push the bar segment below baseline.
            otherRuns: Number.isFinite(providedOtherRuns)
              ? providedOtherRuns
              : Math.max(totalRuns - boundaryRuns, 0),
            fourCount: toNumber(overAttrs.FourCount),
            sixCount: toNumber(overAttrs.SixCount),
          };
        }),
      };
    });

    const pathParts = path.split('/').filter(Boolean);
    return {
      gameId: `${root?._attributes?.Id || pathParts[pathParts.length - 1] || ''}`,
      innings,
    };
  }
}
